import { authSplitApi } from 'redux/helpers/slice.helpers';
import { EPropertyMangerUrl } from 'data/urls/propertyManager.url';
import { EIntegrationUrls } from 'data/urls/integration.url';
import {
  TConfigurePartnerApiPropertyUI,
  TCreatePartnerApiIntegrationUI,
  TCreatePartnerApiWebhookSubscriptionUI,
  TPartnerApiConfiguredPropertyDB,
  TPartnerApiConfiguredPropertyUI,
  TPartnerApiIntegrationAppDB,
  TPartnerApiIntegrationAppUI,
  TPartnerApiIntegrationDB,
  TPartnerApiIntegrationDetailsDB,
  TPartnerApiIntegrationDetailsUI,
  TPartnerApiIntegrationUI,
  TPartnerApiWebhookSubscriptionDB,
  TPartnerApiWebhookSubscriptionUI,
  TRemovePartnerApiWebhookSubscriptionUI,
} from 'data/types/partnerApiIntegration.types';
import {
  configurePartnerApiPropertyConverter,
  createPartnerApiIntegrationConverter,
  createPartnerApiWebhookSubscriptionConverter,
  partnerApiConfiguredPropertyConverter,
  partnerApiIntegrationAppConverter,
  partnerApiIntegrationConverter,
  partnerApiIntegrationDetailsConverter,
  partnerApiWebhookSubscriptionConverter,
} from 'data/converters/partnerApiIntegration.converter';

const baseUrl = `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EIntegrationUrls.PARTNER_API_INTEGRATIONS}`;

export const partnerApiIntegrationsApi = authSplitApi('partnerApiIntegrations', [
  'partnerApiIntegrations',
  'partnerApiConfiguredProperties',
  'partnerApiWebhookSubscriptions',
]).injectEndpoints({
  endpoints: (build) => ({
    getPartnerApiApps: build.query<TPartnerApiIntegrationAppUI[], void>({
      query: () => ({
        url: `${baseUrl}/${EIntegrationUrls.PARTNERS}`,
        method: 'GET',
      }),
      transformResponse: (response: TPartnerApiIntegrationAppDB[]) =>
        response.map(partnerApiIntegrationAppConverter.fromDb),
    }),
    getPartnerApiIntegrations: build.query<TPartnerApiIntegrationUI[], void>({
      query: () => ({
        url: `${baseUrl}/${EIntegrationUrls.CONNECTIONS}`,
        method: 'GET',
      }),
      transformResponse: (response: TPartnerApiIntegrationDB[]) =>
        response.map(partnerApiIntegrationConverter.fromDb),
      providesTags: ['partnerApiIntegrations'],
    }),
    createPartnerApiIntegration: build.mutation<TPartnerApiIntegrationDetailsUI, TCreatePartnerApiIntegrationUI>({
      query: (payload) => ({
        url: baseUrl,
        method: 'POST',
        body: createPartnerApiIntegrationConverter.toDb(payload),
      }),
      transformResponse: (response: TPartnerApiIntegrationDetailsDB) =>
        partnerApiIntegrationDetailsConverter.fromDb(response),
      invalidatesTags: ['partnerApiIntegrations'],
    }),
    deletePartnerApiIntegration: build.mutation<void, string>({
      query: (settingsId) => ({
        url: `${baseUrl}/${settingsId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['partnerApiIntegrations'],
    }),

    getPartnerApiConfiguredProperties: build.query<TPartnerApiConfiguredPropertyUI[], string>({
      query: (partnerIntegrationId) => ({
        url: `${baseUrl}/${partnerIntegrationId}/${EIntegrationUrls.PROPERTIES}`,
        method: 'GET',
      }),
      transformResponse: (response: TPartnerApiConfiguredPropertyDB[]) =>
        response.map(partnerApiConfiguredPropertyConverter.fromDb),
      providesTags: ['partnerApiConfiguredProperties'],
    }),
    configurePartnerApiProperty: build.mutation<void, TConfigurePartnerApiPropertyUI>({
      query: (payload) => ({
        url: `${baseUrl}/${payload.partnerIntegrationId}/${EIntegrationUrls.PROPERTIES}`,
        method: 'POST',
        body: configurePartnerApiPropertyConverter.toDb(payload),
      }),
      invalidatesTags: ['partnerApiConfiguredProperties'],
    }),
    // keyed by the partner-api-integration-property id (top-level, not nested under the integration)
    removePartnerApiConfiguredProperty: build.mutation<void, string>({
      query: (partnerApiIntegrationPropertyId) => ({
        url: `${baseUrl}/${EIntegrationUrls.PROPERTIES}/${partnerApiIntegrationPropertyId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['partnerApiConfiguredProperties'],
    }),
    bulkUpdatePartnerApiProperties: build.mutation<
      void,
      { partnerIntegrationId: string; propertyIds: string[]; allSelected: boolean; nameContains?: string; enabled: boolean }
    >({
      query: ({ partnerIntegrationId, propertyIds, allSelected, nameContains, enabled }) => ({
        url: `${baseUrl}/${partnerIntegrationId}/${EIntegrationUrls.PROPERTIES}/bulk`,
        method: enabled ? 'POST' : 'DELETE',
        body: { propertyIds, allSelected, search: nameContains ? { nameContains } : null },
      }),
      invalidatesTags: ['partnerApiConfiguredProperties'],
    }),

    getPartnerApiWebhookEventTypes: build.query<string[], void>({
      query: () => ({
        url: `${baseUrl}/${EIntegrationUrls.WEBHOOK}/${EIntegrationUrls.EVENT_TYPES}`,
        method: 'GET',
      }),
    }),
    getPartnerApiWebhookSubscriptions: build.query<TPartnerApiWebhookSubscriptionUI[], string>({
      query: (partnerIntegrationId) => ({
        url: `${baseUrl}/${partnerIntegrationId}/${EIntegrationUrls.WEBHOOK}/${EIntegrationUrls.SUBSCRIPTIONS}`,
        method: 'GET',
      }),
      transformResponse: (response: TPartnerApiWebhookSubscriptionDB[]) =>
        response.map(partnerApiWebhookSubscriptionConverter.fromDb),
      providesTags: ['partnerApiWebhookSubscriptions'],
    }),
    createPartnerApiWebhookSubscription: build.mutation<void, TCreatePartnerApiWebhookSubscriptionUI>({
      query: (payload) => ({
        url: `${baseUrl}/${payload.partnerIntegrationId}/${EIntegrationUrls.WEBHOOK}/${EIntegrationUrls.SUBSCRIPTIONS}`,
        method: 'POST',
        body: createPartnerApiWebhookSubscriptionConverter.toDb(payload),
      }),
      invalidatesTags: ['partnerApiWebhookSubscriptions'],
    }),
    removePartnerApiWebhookSubscription: build.mutation<void, TRemovePartnerApiWebhookSubscriptionUI>({
      query: ({ partnerIntegrationId, subscriptionId }) => ({
        url: `${baseUrl}/${partnerIntegrationId}/${EIntegrationUrls.WEBHOOK}/${EIntegrationUrls.SUBSCRIPTIONS}/${subscriptionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['partnerApiWebhookSubscriptions'],
    }),
  }),
});

export const {
  useGetPartnerApiAppsQuery,
  useGetPartnerApiIntegrationsQuery,
  useCreatePartnerApiIntegrationMutation,
  useDeletePartnerApiIntegrationMutation,
  useGetPartnerApiConfiguredPropertiesQuery,
  useConfigurePartnerApiPropertyMutation,
  useRemovePartnerApiConfiguredPropertyMutation,
  useBulkUpdatePartnerApiPropertiesMutation,
  useGetPartnerApiWebhookEventTypesQuery,
  useGetPartnerApiWebhookSubscriptionsQuery,
  useCreatePartnerApiWebhookSubscriptionMutation,
  useRemovePartnerApiWebhookSubscriptionMutation,
} = partnerApiIntegrationsApi;